import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronRight, ChevronLeft, Smartphone, Download, 
  BookOpen, Trophy, Settings, Users, BarChart3, HelpCircle,
  Sparkles, Home, GraduationCap
} from 'lucide-react';

const TOUR_STEPS = {
  parent: [
    {
      id: 'welcome',
      title: 'Welcome to Zazu!',
      description: 'Let us show you around. This quick tour will help you get the most out of your learning experience.',
      icon: Sparkles,
      position: 'center',
      highlight: null
    },
    {
      id: 'children',
      title: 'Manage Your Children',
      description: 'Add and manage your children\'s profiles here. Each child gets their own personalized learning path.',
      icon: Users,
      position: 'bottom',
      highlight: '[data-tour="children-section"]',
      fallbackPosition: { top: '200px', left: '50%' }
    },
    {
      id: 'learning-hub',
      title: 'Learning Hub',
      description: 'Access all learning activities including Reading, Math, Writing, Science, and Bible stories.',
      icon: BookOpen,
      position: 'bottom',
      highlight: '[data-tour="learning-hub"]',
      fallbackPosition: { top: '300px', left: '50%' }
    },
    {
      id: 'progress',
      title: 'Track Progress',
      description: 'View detailed progress reports to see how your children are doing in each subject.',
      icon: BarChart3,
      position: 'left',
      highlight: '[data-tour="progress-section"]',
      fallbackPosition: { top: '250px', right: '100px' }
    },
    {
      id: 'badges',
      title: 'Earn Rewards',
      description: 'Children earn badges, XP points, and climb the leaderboard as they complete activities!',
      icon: Trophy,
      position: 'bottom',
      highlight: '[data-tour="badges-section"]',
      fallbackPosition: { top: '350px', left: '50%' }
    },
    {
      id: 'settings',
      title: 'Settings & Help',
      description: 'Customize your experience, manage subscriptions, and get help from our support team.',
      icon: Settings,
      position: 'top',
      highlight: '[data-tour="settings-button"]',
      fallbackPosition: { bottom: '100px', right: '50px' }
    },
    {
      id: 'mobile-app',
      title: 'Install Mobile App',
      description: 'Want to learn on the go? Install Zazu as an app on your phone or tablet!',
      icon: Smartphone,
      position: 'center',
      highlight: null,
      showInstallGuide: true
    }
  ],
  student: [
    {
      id: 'welcome',
      title: 'Welcome, Learner!',
      description: 'Ready to start your learning adventure? Let us show you around!',
      icon: Sparkles,
      position: 'center',
      highlight: null
    },
    {
      id: 'subjects',
      title: 'Choose Your Subject',
      description: 'Pick what you want to learn today - Reading, Math, Writing, or Science!',
      icon: BookOpen,
      position: 'bottom',
      highlight: '[data-tour="subjects-grid"]',
      fallbackPosition: { top: '200px', left: '50%' }
    },
    {
      id: 'achievements',
      title: 'Your Achievements',
      description: 'See your badges and XP points here. Keep learning to earn more rewards!',
      icon: Trophy,
      position: 'right',
      highlight: '[data-tour="achievements"]',
      fallbackPosition: { top: '150px', right: '50px' }
    },
    {
      id: 'mobile-app',
      title: 'Learn Anywhere!',
      description: 'Ask a parent to install the Zazu app on a tablet so you can learn anywhere!',
      icon: Smartphone,
      position: 'center',
      highlight: null
    }
  ],
  teacher: [
    {
      id: 'welcome',
      title: 'Welcome, Teacher!',
      description: 'Here\'s a quick tour of your classroom management dashboard.',
      icon: GraduationCap,
      position: 'center',
      highlight: null
    },
    {
      id: 'students',
      title: 'Your Students',
      description: 'View and manage all students in your class. Track their individual progress.',
      icon: Users,
      position: 'bottom',
      highlight: '[data-tour="students-list"]',
      fallbackPosition: { top: '200px', left: '50%' }
    },
    {
      id: 'assignments',
      title: 'Assignments',
      description: 'Create and assign activities to your students. Set due dates and track completion.',
      icon: BookOpen,
      position: 'right',
      highlight: '[data-tour="assignments"]',
      fallbackPosition: { top: '300px', left: '50%' }
    },
    {
      id: 'reports',
      title: 'Class Reports',
      description: 'View comprehensive reports on class performance and identify students who need help.',
      icon: BarChart3,
      position: 'left',
      highlight: '[data-tour="reports"]',
      fallbackPosition: { top: '250px', right: '100px' }
    },
    {
      id: 'mobile-app',
      title: 'Install Mobile App',
      description: 'Access your dashboard on the go with our mobile app!',
      icon: Smartphone,
      position: 'center',
      highlight: null,
      showInstallGuide: true
    }
  ]
};

const InstallGuide = () => {
  const [platform, setPlatform] = useState('ios');
  
  return (
    <div className="mt-4 space-y-4">
      <div className="flex gap-2 justify-center">
        <Button 
          size="sm" 
          variant={platform === 'ios' ? 'default' : 'outline'}
          onClick={() => setPlatform('ios')}
          className="text-xs"
        >
          iPhone/iPad
        </Button>
        <Button 
          size="sm" 
          variant={platform === 'android' ? 'default' : 'outline'}
          onClick={() => setPlatform('android')}
          className="text-xs"
        >
          Android
        </Button>
        <Button 
          size="sm" 
          variant={platform === 'desktop' ? 'default' : 'outline'}
          onClick={() => setPlatform('desktop')}
          className="text-xs"
        >
          Desktop
        </Button>
      </div>
      
      <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-sm">
        {platform === 'ios' && (
          <ol className="space-y-2 text-left">
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
              <span>Open Zazu in <strong>Safari</strong> browser</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
              <span>Tap the <strong>Share</strong> button (square with arrow)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
              <span>Scroll and tap <strong>"Add to Home Screen"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
              <span>Tap <strong>"Add"</strong> - Zazu icon will appear on your home screen!</span>
            </li>
          </ol>
        )}
        {platform === 'android' && (
          <ol className="space-y-2 text-left">
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
              <span>Open Zazu in <strong>Chrome</strong> browser</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
              <span>Tap the <strong>three dots</strong> menu (top right)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
              <span>Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
              <span>Tap <strong>"Install"</strong> - Zazu will be added to your apps!</span>
            </li>
          </ol>
        )}
        {platform === 'desktop' && (
          <ol className="space-y-2 text-left">
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
              <span>Open Zazu in <strong>Chrome</strong> or <strong>Edge</strong> browser</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
              <span>Look for the <strong>install icon</strong> in the address bar (right side)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
              <span>Click <strong>"Install"</strong> in the popup</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
              <span>Zazu will open as a standalone app!</span>
            </li>
          </ol>
        )}
      </div>
    </div>
  );
};

const OnboardingTour = ({ userType = 'parent', onComplete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: '50%', left: '50%' });
  
  const steps = TOUR_STEPS[userType] || TOUR_STEPS.parent;
  const currentStepData = steps[currentStep];
  
  useEffect(() => {
    // Check if user has seen the tour
    const tourKey = `zazu_tour_completed_${userType}`;
    const hasSeenTour = localStorage.getItem(tourKey);
    
    if (!hasSeenTour) {
      // Delay showing tour to let page render
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userType]);
  
  useEffect(() => {
    if (!isVisible || !currentStepData) return;
    
    const updatePosition = () => {
      if (currentStepData.position === 'center' || !currentStepData.highlight) {
        setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
        return;
      }
      
      const element = document.querySelector(currentStepData.highlight);
      if (element) {
        const rect = element.getBoundingClientRect();
        const pos = {};
        
        switch (currentStepData.position) {
          case 'bottom':
            pos.top = `${rect.bottom + 16}px`;
            pos.left = `${rect.left + rect.width / 2}px`;
            pos.transform = 'translateX(-50%)';
            break;
          case 'top':
            pos.bottom = `${window.innerHeight - rect.top + 16}px`;
            pos.left = `${rect.left + rect.width / 2}px`;
            pos.transform = 'translateX(-50%)';
            break;
          case 'left':
            pos.top = `${rect.top + rect.height / 2}px`;
            pos.right = `${window.innerWidth - rect.left + 16}px`;
            pos.transform = 'translateY(-50%)';
            break;
          case 'right':
            pos.top = `${rect.top + rect.height / 2}px`;
            pos.left = `${rect.right + 16}px`;
            pos.transform = 'translateY(-50%)';
            break;
          default:
            pos.top = '50%';
            pos.left = '50%';
            pos.transform = 'translate(-50%, -50%)';
        }
        
        setTooltipPosition(pos);
        
        // Add highlight effect to element
        element.classList.add('tour-highlight');
      } else if (currentStepData.fallbackPosition) {
        setTooltipPosition({ ...currentStepData.fallbackPosition, transform: 'translateX(-50%)' });
      }
    };
    
    updatePosition();
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      // Remove highlight from all elements
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });
    };
  }, [currentStep, isVisible, currentStepData]);
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleComplete = useCallback(() => {
    const tourKey = `zazu_tour_completed_${userType}`;
    localStorage.setItem(tourKey, 'true');
    setIsVisible(false);
    onComplete?.();
  }, [userType, onComplete]);
  
  const handleSkip = () => {
    handleComplete();
  };
  
  if (!isVisible || !currentStepData) return null;
  
  const IconComponent = currentStepData.icon;
  
  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={handleSkip}
      />
      
      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -20 }}
          transition={{ duration: 0.3 }}
          className="fixed z-[101] w-[90%] max-w-md"
          style={tooltipPosition}
          data-testid="onboarding-tooltip"
        >
          <Card className="shadow-2xl border-2 border-emerald-200 dark:border-emerald-700">
            <CardContent className="p-6">
              {/* Close button */}
              <button
                onClick={handleSkip}
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Close tour"
                data-testid="tour-close"
              >
                <X className="w-5 h-5" />
              </button>
              
              {/* Icon and title */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-800 dark:text-white">
                    {currentStepData.title}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Step {currentStep + 1} of {steps.length}
                  </p>
                </div>
              </div>
              
              {/* Description */}
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                {currentStepData.description}
              </p>
              
              {/* Install guide for mobile step */}
              {currentStepData.showInstallGuide && <InstallGuide />}
              
              {/* Progress dots */}
              <div className="flex justify-center gap-1.5 mb-4">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentStep 
                        ? 'bg-emerald-500 w-6' 
                        : index < currentStep 
                          ? 'bg-emerald-300' 
                          : 'bg-slate-300'
                    }`}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-between items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  className="text-slate-500"
                  data-testid="tour-skip"
                >
                  Skip tour
                </Button>
                
                <div className="flex gap-2">
                  {currentStep > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrev}
                      data-testid="tour-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleNext}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="tour-next"
                  >
                    {currentStep === steps.length - 1 ? (
                      <>
                        Get Started
                        <Sparkles className="w-4 h-4 ml-1" />
                      </>
                    ) : (
                      <>
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
      
      {/* Global styles for tour highlight */}
      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 99;
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.3);
          border-radius: 8px;
          animation: pulse-highlight 2s infinite;
        }
        
        @keyframes pulse-highlight {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.3);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.2), 0 0 30px rgba(16, 185, 129, 0.4);
          }
        }
      `}</style>
    </>
  );
};

// Hook to manually trigger tour
export const useTour = (userType) => {
  const resetTour = useCallback(() => {
    const tourKey = `zazu_tour_completed_${userType}`;
    localStorage.removeItem(tourKey);
    window.location.reload();
  }, [userType]);
  
  return { resetTour };
};

export default OnboardingTour;
